import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper, Typography, Box, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Tooltip, TextField, MenuItem, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, TablePagination,
  Divider, useMediaQuery, Switch,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  Business as CompanyIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// ───── Mobile design tokens ─────
const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48 };
const TT = { min: 44, primary: 48, hero: 56 };
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  cardBg:         "#ffffff",
};
const MOBILE_INPUT_PROPS = { style: { fontSize: 16 } };

const T = (key, opts) => i18n.t(`subscriptionsManager.${key}`, opts) || key;

// Status → stripe + pill color resolver. Status drives both the 4px
// left stripe and the top-right pill. Kept outside useStyles so JSX can
// inline `style={{ '--stripe': color }}` against the card.
const STATUS_THEME = {
  active:    { stripe: "#25d366", pillBg: "#25d366", pillFg: "#ffffff" },
  trial:     { stripe: "#f59e0b", pillBg: "#f59e0b", pillFg: "#ffffff" },
  past_due:  { stripe: "#ef4444", pillBg: "#ef4444", pillFg: "#ffffff" },
  cancelled: { stripe: "#94a3b8", pillBg: "#94a3b8", pillFg: "#ffffff" },
  expired:   { stripe: "#64748b", pillBg: "#64748b", pillFg: "#ffffff" },
  pending:   { stripe: "#f59e0b", pillBg: "#f59e0b", pillFg: "#ffffff" },
  success:   { stripe: "#25d366", pillBg: "#25d366", pillFg: "#ffffff" },
  failed:    { stripe: "#ef4444", pillBg: "#ef4444", pillFg: "#ffffff" },
};
const resolveStatusTheme = (s) => STATUS_THEME[s] || STATUS_THEME.pending;

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  return {
    root: {
      padding: 0,
      // Containment — without these the inner 720-px table can push
      // the SubscriptionsManager root past the panelCard's grid cell,
      // which is what was causing the toolbar/action buttons to wrap
      // at viewport width instead of card width.
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      minWidth: 0,
      overflow: "hidden",
    },
    // Brand-icon-pill + title + actions header, matches the other
    // Settings panels after the redesign.
    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 16,
      gap: 12,
      flexWrap: "wrap",
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      minWidth: 0,
      // Below sm (<960 px) the three action buttons (Refresh / Record
      // Payment / Assign Plan) plus the brand pill + title don't fit
      // on one row. Stack the header vertically so the actions row
      // stays inside the card boundary instead of escaping it.
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: SP.sm,
      },
    },
    titleBox: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      // Allow the title to shrink before the actions wrap. Without
      // flex:1/minWidth:0 the title forces the actionsRow to wrap as
      // a whole at constrained widths.
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "hidden",
    },
    headerIconPill: {
      width: 38,
      height: 38,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(46,227,122,0.12)" : "rgba(37,211,102,0.12)",
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: isDark ? "#7df0a8" : "#128c7e",
      },
    },
    titleText: {
      fontSize: 17,
      fontWeight: 700,
      color: isDark ? "#f1f5f9" : "#0f172a",
      lineHeight: 1.2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    titleSubtitle: {
      fontSize: 12.5,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.55)" : "#94a3b8",
      marginTop: 2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    actionsRow: {
      display: "flex",
      gap: 8,
      flexShrink: 0,
      flexWrap: "wrap",
      justifyContent: "flex-end",
      // Below sm: full-width column stack so buttons stay inside the
      // card. Each button gets minHeight 48 per the touch-target rule.
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        width: "100%",
        justifyContent: "stretch",
        "& > *": {
          width: "100%",
          minHeight: TT.primary,
        },
      },
    },
    refreshBtn: {
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
      color: isDark ? "rgba(255,255,255,0.78)" : "#475569",
      fontWeight: 600,
      padding: "6px 14px",
      borderRadius: 10,
      textTransform: "none",
      justifyContent: "center",
      whiteSpace: "nowrap",
      // Per-class startIcon/endIcon spacing — JSS class specificity
      // outranks the global MuiButton theme override (commit 3f243c1e)
      // which means the leading-icon gap is otherwise lost on this
      // component and the icon collides with the label.
      "& .MuiButton-startIcon": {
        marginLeft: theme.direction === "rtl" ? 8 : 0,
        marginRight: theme.direction === "rtl" ? 0 : 8,
        display: "inline-flex",
        alignItems: "center",
      },
      "& .MuiButton-endIcon": {
        marginLeft: theme.direction === "rtl" ? 0 : 8,
        marginRight: theme.direction === "rtl" ? 8 : 0,
        display: "inline-flex",
        alignItems: "center",
      },
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.08)",
      },
      [theme.breakpoints.down("sm")]: { minHeight: TT.primary },
    },
    ghostBtn: {
      background: isDark ? "rgba(46,227,122,0.10)" : "rgba(37,211,102,0.10)",
      color: isDark ? "#7df0a8" : "#128c7e",
      fontWeight: 700,
      padding: "6px 14px",
      borderRadius: 10,
      textTransform: "none",
      justifyContent: "center",
      whiteSpace: "nowrap",
      "& .MuiButton-startIcon": {
        marginLeft: theme.direction === "rtl" ? 8 : 0,
        marginRight: theme.direction === "rtl" ? 0 : 8,
        display: "inline-flex",
        alignItems: "center",
      },
      "& .MuiButton-endIcon": {
        marginLeft: theme.direction === "rtl" ? 0 : 8,
        marginRight: theme.direction === "rtl" ? 8 : 0,
        display: "inline-flex",
        alignItems: "center",
      },
      "&:hover": {
        background: isDark ? "rgba(46,227,122,0.18)" : "rgba(37,211,102,0.18)",
      },
      [theme.breakpoints.down("sm")]: { minHeight: TT.primary },
    },
    primaryBtn: {
      background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
      color: "#fff",
      fontWeight: 700,
      padding: "6px 16px",
      borderRadius: 10,
      textTransform: "none",
      justifyContent: "center",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 12px rgba(37, 211, 102, 0.28)",
      "& .MuiButton-startIcon": {
        marginLeft: theme.direction === "rtl" ? 8 : 0,
        marginRight: theme.direction === "rtl" ? 0 : 8,
        display: "inline-flex",
        alignItems: "center",
      },
      "& .MuiButton-endIcon": {
        marginLeft: theme.direction === "rtl" ? 0 : 8,
        marginRight: theme.direction === "rtl" ? 8 : 0,
        display: "inline-flex",
        alignItems: "center",
      },
      "&:hover": {
        background: "linear-gradient(135deg, #1ebe5d 0%, #075e54 100%)",
      },
      [theme.breakpoints.down("sm")]: { minHeight: TT.primary },
    },
    tableContainer: {
      borderRadius: 14,
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      background: isDark ? "#0F1210" : "#fff",
      boxShadow: "none",
      // Width clamps so the inner 720-px table cannot push the
      // container past the panelCard. Pairs with root/toolbar
      // containment to keep the header action buttons inside the
      // card boundary at all viewport widths.
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      minWidth: 0,
      // 8-column subscription table doesn't fit on phone width. Let
      // the container scroll horizontally on touch instead of
      // squishing the columns or overflowing the page. `overflow:
      // hidden` was masking the table edge but also blocking the
      // built-in MUI TableContainer scroll behaviour.
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      "& table": {
        minWidth: 720,
      },
    },
    tableHeadRow: {
      backgroundColor: isDark ? "rgba(46,227,122,0.06)" : "rgba(37,211,102,0.06)",
      "& th": {
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: 0.5,
        color: isDark ? "#7df0a8" : "#128c7e",
        textTransform: "uppercase",
        borderBottom: isDark
          ? "1px solid rgba(46,227,122,0.18)"
          : "1px solid rgba(37,211,102,0.22)",
      },
    },
    tableRow: {
      transition: "background-color 120ms ease",
      "&:hover": {
        backgroundColor: isDark ? "rgba(46,227,122,0.06)" : "rgba(37,211,102,0.04)",
      },
    },
    statusChip: {
      fontWeight: 700,
      fontSize: 10.5,
      letterSpacing: 0.3,
      height: 22,
      borderRadius: 999,
    },
    inputControl: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        background: isDark ? "#131614" : "#f8fafc",
        fontSize: 14,
        fontWeight: 500,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.08)"
          : "rgba(15,23,42,0.08)",
      },
      "& .MuiSelect-select": { paddingTop: 10, paddingBottom: 10 },
    },
    dialogField: {
      marginBottom: theme.spacing(2),
    },
    emptyCell: {
      padding: 32,
      color: isDark ? "rgba(255,255,255,0.5)" : "#9ca3af",
    },
    // Hides the table chrome on xs — the mobile path renders a
    // dedicated card stack (see mobileCatalog below) instead of trying
    // to reflow the 5-col table. Cleaner info hierarchy per Raviv's
    // feedback that the subscription cards were cramped.
    desktopOnlyTable: {
      [theme.breakpoints.down("xs")]: {
        display: "none",
      },
    },
    // ── Mobile card stack: vertical stack of subscription cards ──
    // One subscription = one card. Visual structure:
    //   ┌──────────────────────────────────┐
    //   │▌ Tenant name        [ACTIVE pill]│  ← stripe + header row
    //   │▌ PLAN NAME (accent meta)         │
    //   │▌                                 │
    //   │▌ ₪199.00                         │  ← price (bold 18)
    //   │▌ per month • renews 12 Jun 2026  │  ← muted meta
    //   │▌ ─────────────────────────────── │  ← hairline
    //   │▌  [View]  [Edit]  [Cancel]       │  ← outlined actions
    //   └──────────────────────────────────┘
    mobileCatalog: {
      display: "none",
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        gap: SP.sm,
        padding: "8px 0 24px",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        minWidth: 0,
      },
    },
    // === LINEAR-DENSE MOBILE CARD (xs override) ===
    // Spec: 12px padding, 1px border rgba(0,0,0,0.06), no resting shadow,
    // 14px title, 12px values, 28px hit-area icons, status dot top-left.
    mobileSubCard: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      background: isDark ? "#13181a" : "#ffffff",
      borderRadius: 8,
      border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      // No resting shadow per spec
      boxShadow: "none",
      overflow: "hidden",
      transition: "border-color 0.14s ease, box-shadow 0.14s ease",
      "&:hover": {
        borderColor: isDark ? "rgba(255,255,255,0.14)" : "#d4d4d4",
        boxShadow: "0 1px 2px rgba(10,10,10,0.04)",
      },
      // Status dot top-left (6-8px circle for at-a-glance health)
      "&::before": {
        content: '""',
        position: "absolute",
        insetInlineStart: 12,
        top: 14,
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "var(--stripe, #a3a3a3)",
      },
      "@media (max-width: 360px)": {
        borderRadius: 8,
      },
    },
    mobileSubHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "12px 14px 6px 26px",
      minWidth: 0,
      "@media (max-width: 360px)": {
        padding: "10px 12px 4px 24px",
      },
    },
    mobileSubHeaderText: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
      flex: "1 1 auto",
    },
    mobileSubTenant: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: "18px",
      color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
      letterSpacing: "-0.005em",
      // NEVER truncate
      overflow: "visible",
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "anywhere",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 6,
      minWidth: 0,
      "& .MuiSvgIcon-root": {
        fontSize: 14,
        color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
        flexShrink: 0,
      },
      "@media (max-width: 360px)": {
        fontSize: 13,
        lineHeight: "17px",
      },
    },
    mobileSubPlanMeta: {
      fontSize: 11,
      fontWeight: 400,
      letterSpacing: 0,
      textTransform: "none",
      color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
      // No truncation
      overflow: "visible",
      whiteSpace: "normal",
      "@media (max-width: 360px)": {
        fontSize: 10,
      },
    },
    mobileSubStatusPill: {
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      height: "auto",
      padding: "2px 6px",
      borderRadius: 4,
      fontSize: 10,
      lineHeight: "13px",
      fontWeight: 500,
      letterSpacing: "0.01em",
      textTransform: "none",
      background: "var(--pill-bg, #f4f4f5)",
      color: "var(--pill-fg, #525252)",
      whiteSpace: "nowrap",
    },
    mobileSubBody: {
      display: "flex",
      flexDirection: "column",
      gap: 3,
      padding: "4px 14px 10px 26px",
      "@media (max-width: 360px)": {
        padding: "3px 12px 8px 24px",
      },
    },
    mobileSubPrice: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: "18px",
      color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
      fontFeatureSettings: '"tnum" 1',
      letterSpacing: "-0.005em",
    },
    mobileSubMuted: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "16px",
      color: isDark ? "rgba(255,255,255,0.55)" : "#71717a",
      display: "flex",
      alignItems: "center",
      gap: 6,
      "& .MuiSvgIcon-root": {
        fontSize: 13,
        color: isDark ? "rgba(255,255,255,0.45)" : "#a3a3a3",
        flexShrink: 0,
      },
    },
    mobileSubActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 2,
      padding: "8px 12px",
      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#ededed"}`,
    },
    // Compact icon-button-style action — visible glyph stays 16px / ~28x28
    // visual footprint, but hit area expands to 44x44 on xs per WCAG /
    // iOS HIG tap-target rule (Raviv 2026-06-05; same pattern as
    // pages/Connections tableIconBtn).
    mobileSubActionBtn: {
      minWidth: 0,
      width: 28,
      height: 28,
      padding: 6,
      borderRadius: 4,
      border: "none",
      background: "transparent",
      color: isDark ? "rgba(255,255,255,0.55)" : "#a3a3a3",
      fontWeight: 500,
      fontSize: 11,
      letterSpacing: 0,
      textTransform: "none",
      "& .MuiButton-startIcon": {
        margin: 0,
        "& .MuiSvgIcon-root": { fontSize: 16 },
      },
      "& .MuiButton-label": {
        // Label hidden — icon-only on xs
        fontSize: 0,
      },
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "#f4f4f5",
        color: isDark ? "rgba(255,255,255,0.92)" : "#0a0a0a",
        border: "none",
      },
      "&[data-variant='danger']": {
        color: "#dc2626",
        "&:hover": {
          background: isDark ? "rgba(255,107,107,0.08)" : "rgba(220,38,38,0.06)",
          color: "#dc2626",
          border: "none",
        },
      },
      // Tap-friendly target on phones (≥44px per WCAG / iOS HIG). Icon
      // glyph stays 16px — only the click target grows, via minWidth /
      // minHeight + extra padding (same pattern as pages/Connections
      // tableIconBtn, lines 316-321).
      [theme.breakpoints.down("xs")]: {
        minWidth: TT.min,
        minHeight: TT.min,
        padding: 10,
      },
    },
    // Empty state for the mobile card stack — centered accent meta +
    // brand-green CTA.
    mobileEmptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 14,
      padding: "48px 24px 32px",
      textAlign: "center",
      borderRadius: 16,
      border: `1px solid ${hair}`,
      background: isDark ? "#13181a" : "#ffffff",
    },
    mobileEmptyMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
    },
    mobileEmptyDesc: {
      fontSize: 14,
      lineHeight: 1.5,
      color: fgMuted,
      maxWidth: 240,
      margin: 0,
    },
    mobileEmptyCta: {
      minHeight: TT.primary,
      paddingInline: 22,
      borderRadius: 12,
      background: COL.brandGreen,
      color: "#fff",
      fontWeight: 700,
      textTransform: "none",
      fontSize: 14,
      letterSpacing: 0.1,
      "&:hover": {
        background: "#1ebe5d",
      },
    },
    "@keyframes subCardIn": {
      "0%": { opacity: 0, transform: "translateY(8px)" },
      "100%": { opacity: 1, transform: "translateY(0)" },
    },
    // xs: action icons bumped to ≥44px tap zone.
    actionCellMobile: {
      [theme.breakpoints.down("xs")]: {
        "& .MuiIconButton-root": {
          minWidth: TT.min,
          minHeight: TT.min,
          padding: 10,
        },
      },
    },
    // Toolbar actions stack full-width on xs (B.2).
    actionsRowMobile: {
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        width: "100%",
        "& > *": { width: "100%", minHeight: TT.primary },
      },
    },
    // DialogActions stack column-reverse on xs (B.5).
    dialogActionsMobile: {
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column-reverse",
        padding: SP.md,
        gap: SP.xs,
        "& > *": {
          width: "100%",
          minHeight: TT.primary,
          marginInlineStart: "0 !important",
        },
      },
    },
    // TablePagination: cramped on xs — hide rows-per-page label.
    paginationMobile: {
      [theme.breakpoints.down("xs")]: {
        "& .MuiTablePagination-caption:first-of-type": { display: "none" },
        "& .MuiTablePagination-selectRoot": { marginInlineEnd: SP.sm },
      },
    },
  };
});

// Brand-aligned status palette — replaces the prior #dbeafe / #fef3c7
// / #fee2e2 / #f3f4f6 mix with tinted hairline pills that share the
// rest of the redesigned Settings page.
const statusColors = {
  trial: { bg: "rgba(46, 227, 122, 0.10)", color: "#0d8474", border: "rgba(46, 227, 122, 0.28)" },
  active: { bg: "rgba(37, 211, 102, 0.14)", color: "#128c7e", border: "rgba(37, 211, 102, 0.28)" },
  past_due: { bg: "rgba(245, 158, 11, 0.14)", color: "#b45309", border: "rgba(245, 158, 11, 0.28)" },
  cancelled: { bg: "rgba(239, 68, 68, 0.12)", color: "#dc2626", border: "rgba(239, 68, 68, 0.28)" },
  expired: { bg: "rgba(148, 163, 184, 0.16)", color: "#64748b", border: "rgba(148, 163, 184, 0.32)" },
  success: { bg: "rgba(37, 211, 102, 0.14)", color: "#128c7e", border: "rgba(37, 211, 102, 0.28)" },
  failed: { bg: "rgba(239, 68, 68, 0.12)", color: "#dc2626", border: "rgba(239, 68, 68, 0.28)" },
  pending: { bg: "rgba(245, 158, 11, 0.14)", color: "#b45309", border: "rgba(245, 158, 11, 0.28)" },
};

const StatusChip = ({ status }) => {
  const classes = useStyles();
  const config = statusColors[status] || statusColors.pending;
  return (
    <Chip
      size="small"
      label={status?.replace("_", " ").toUpperCase()}
      className={classes.statusChip}
      style={{
        background: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
      }}
    />
  );
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const formatCurrency = (a, c = "ILS") => {
  const n = parseFloat(a) || 0;
  const symbols = { ILS: "₪", USD: "$", EUR: "€" };
  return `${symbols[c] || ""}${n.toFixed(2)}`;
};

const SubscriptionsManager = () => {
  const classes = useStyles();
  const theme = useTheme();
  // fullScreen on xs for both Dialogs (assign + payment) — each has 4-5 form
  // fields and was clipping at the maxWidth='sm' centered modal on 360px.
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubs] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, total: 0 });
  const [plans, setPlans] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  // Per-row in-flight guard for the auto-renew toggle — a Set of the
  // subscription ids currently being saved. A scalar would leak across
  // rows: toggling row B while row A is still in flight, then A
  // resolving, would clear the guard and re-enable B's Switch mid-
  // request. The Set releases each row independently so a double-write
  // to the same row can't slip through.
  const [autoRenewSavingIds, setAutoRenewSavingIds] = useState(() => new Set());

  const [form, setForm] = useState({ companyId: "", planId: "", status: "active", periodMonths: 1 });
  const [payForm, setPayForm] = useState({ companyId: "", amount: "", currency: "ILS", description: "", type: "subscription" });

  const loadSubs = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const [subRes, planRes] = await Promise.all([
        api.get(`/billing/admin/subscriptions?page=${page + 1}&limit=15`),
        api.get("/billing/plans"),
      ]);
      setSubs(subRes.data.subscriptions || []);
      setPagination({ page, total: subRes.data.pagination?.total || 0 });
      setPlans(planRes.data.plans || []);
    } catch {
      toast.error(T("loadFailed"));
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  const handleAssign = async () => {
    try {
      await api.post("/billing/admin/subscriptions", form);
      toast.success(T("assignSuccess"));
      setAssignOpen(false);
      setForm({ companyId: "", planId: "", status: "active", periodMonths: 1 });
      loadSubs(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.error || T("assignFailed"));
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(T("cancelConfirm"))) return;
    try {
      await api.delete(`/billing/admin/subscriptions/${id}`);
      toast.success(T("cancelSuccess"));
      loadSubs(pagination.page);
    } catch {
      toast.error(T("cancelFailed"));
    }
  };

  // Super-admin override: flip auto-renew on any tenant's subscription.
  // Optimistic — update local state first, revert on failure. Hits
  // POST /billing/admin/subscriptions/:id/auto-renew (isSuper-gated).
  const handleToggleAutoRenew = async (id, next) => {
    setAutoRenewSavingIds((prev) => new Set(prev).add(id));
    setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, autoRenew: next } : s)));
    try {
      await api.post(`/billing/admin/subscriptions/${id}/auto-renew`, { autoRenew: next });
      toast.success(next ? T("autoRenewOn") : T("autoRenewOff"));
    } catch (err) {
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, autoRenew: !next } : s)));
      toast.error(err.response?.data?.error || T("autoRenewError"));
    } finally {
      setAutoRenewSavingIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  };

  const handleRecordPayment = async () => {
    try {
      await api.post("/billing/admin/payments", payForm);
      toast.success(T("paymentSuccess"));
      setPaymentOpen(false);
      setPayForm({ companyId: "", amount: "", currency: "ILS", description: "", type: "subscription" });
      loadSubs(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.error || T("paymentFailed"));
    }
  };

  if (loading && subscriptions.length === 0) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Box className={classes.titleBox}>
          <span className={classes.headerIconPill}><CompanyIcon /></span>
          <div>
            <div className={classes.titleText}>{T("title")}</div>
            <div className={classes.titleSubtitle}>
              {T("subtitle") || `${pagination.total || subscriptions.length} subscriptions`}
            </div>
          </div>
        </Box>
        <Box className={`${classes.actionsRow} ${classes.actionsRowMobile}`}>
          <Button
            className={classes.refreshBtn}
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => loadSubs(pagination.page)}
          >
            {T("refresh")}
          </Button>
          <Button
            className={classes.ghostBtn}
            size="small"
            startIcon={<PaymentIcon />}
            onClick={() => setPaymentOpen(true)}
          >
            {T("recordPayment")}
          </Button>
          <Button
            className={classes.primaryBtn}
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAssignOpen(true)}
          >
            {T("assignPlan")}
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        className={`${classes.tableContainer} ${classes.desktopOnlyTable}`}
        elevation={0}
      >
        <Table size="small">
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell>{T("table.company")}</TableCell>
              <TableCell>{T("table.plan")}</TableCell>
              <TableCell>{T("table.status")}</TableCell>
              <TableCell>{T("table.period")}</TableCell>
              <TableCell align="center">{T("table.autoRenew")}</TableCell>
              <TableCell align="right">{T("table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" className={classes.emptyCell}>
                  {T("noSubscriptions")}
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((s) => (
                <TableRow key={s.id} className={classes.tableRow}>
                  <TableCell data-label={T("table.company")}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <CompanyIcon fontSize="small" color="action" />
                      {s.company?.name || `Company #${s.companyId}`}
                    </Box>
                  </TableCell>
                  <TableCell data-label={T("table.plan")}>{s.plan?.name || `Plan #${s.planId}`}</TableCell>
                  <TableCell data-label={T("table.status")}><StatusChip status={s.status} /></TableCell>
                  <TableCell data-label={T("table.period")}>
                    {formatDate(s.currentPeriodStart)} — {formatDate(s.currentPeriodEnd)}
                  </TableCell>
                  <TableCell data-label={T("table.autoRenew")} align="center">
                    {s.status === "cancelled" || s.status === "expired" ? (
                      <span style={{ color: "#9ca3af" }}>—</span>
                    ) : (
                      <Tooltip title={s.autoRenew ? T("autoRenewOnHint") : T("autoRenewOffHint")}>
                        <Switch
                          size="small"
                          color="primary"
                          checked={!!s.autoRenew}
                          disabled={autoRenewSavingIds.has(s.id)}
                          onChange={(e) => handleToggleAutoRenew(s.id, e.target.checked)}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell data-label="" align="right" className={classes.actionCellMobile}>
                    {s.status !== "cancelled" && (
                      <Tooltip title={T("cancel")}>
                        <IconButton size="small" onClick={() => handleCancel(s.id)}>
                          <CancelIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {pagination.total > 15 && (
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(_, p) => loadSubs(p)}
            rowsPerPage={15}
            rowsPerPageOptions={[15]}
            className={classes.paginationMobile}
          />
        )}
      </TableContainer>

      {/* Mobile card stack — single-column subscription cards with
          status-driven left stripe, status pill, price + renewal
          meta, and outlined action buttons. Replaces the previous
          cardStackMode table reflow that produced cramped 2-up
          grid cells. */}
      <div className={classes.mobileCatalog}>
        {subscriptions.length === 0 ? (
          <div className={classes.mobileEmptyState}>
            <div className={classes.mobileEmptyMeta}>{T("noSubscriptions")}</div>
            <p className={classes.mobileEmptyDesc}>
              {T("emptyDescription") || "Assign a plan to a tenant to create the first subscription."}
            </p>
            <Button
              className={classes.mobileEmptyCta}
              startIcon={<AddIcon />}
              onClick={() => setAssignOpen(true)}
            >
              {T("assignPlan")}
            </Button>
          </div>
        ) : (
          subscriptions.map((s, i) => {
            const sTheme = resolveStatusTheme(s.status);
            const price = s.plan?.value != null
              ? formatCurrency(s.plan.value, s.plan?.currency || "ILS")
              : null;
            const cancelled = s.status === "cancelled";
            return (
              <div
                key={s.id}
                className={classes.mobileSubCard}
                style={{
                  "--i": i,
                  "--stripe": sTheme.stripe,
                }}
              >
                <div className={classes.mobileSubHeader}>
                  <div className={classes.mobileSubHeaderText}>
                    <div className={classes.mobileSubTenant}>
                      <CompanyIcon />
                      {s.company?.name || `Company #${s.companyId}`}
                    </div>
                    <div className={classes.mobileSubPlanMeta}>
                      {s.plan?.name || `Plan #${s.planId}`}
                    </div>
                  </div>
                  <span
                    className={classes.mobileSubStatusPill}
                    style={{
                      "--pill-bg": sTheme.pillBg,
                      "--pill-fg": sTheme.pillFg,
                    }}
                  >
                    {(s.status || "pending").replace("_", " ")}
                  </span>
                </div>
                <div className={classes.mobileSubBody}>
                  {price && <div className={classes.mobileSubPrice}>{price}</div>}
                  <div className={classes.mobileSubMuted}>
                    {s.status === "trial" ? (T("trialBilling") || "Trial period") : (s.plan?.billingInterval === "yearly" ? (T("perYear") || "per year") : (T("perMonth") || "per month"))}
                  </div>
                  <div className={classes.mobileSubMuted}>
                    {(T("renews") || "Renews")} {formatDate(s.currentPeriodEnd)}
                  </div>
                  {s.status !== "cancelled" && s.status !== "expired" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 4,
                      }}
                    >
                      <span className={classes.mobileSubMuted}>
                        {T("table.autoRenew")}
                      </span>
                      <Switch
                        size="small"
                        color="primary"
                        checked={!!s.autoRenew}
                        disabled={autoRenewSavingIds.has(s.id)}
                        onChange={(e) => handleToggleAutoRenew(s.id, e.target.checked)}
                      />
                    </div>
                  )}
                </div>
                {!cancelled && (
                  <div className={classes.mobileSubActions}>
                    <Button
                      className={classes.mobileSubActionBtn}
                      data-variant="danger"
                      onClick={() => handleCancel(s.id)}
                      startIcon={<CancelIcon style={{ fontSize: 14 }} />}
                    >
                      {T("cancel")}
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
        {pagination.total > 15 && (
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(_, p) => loadSubs(p)}
            rowsPerPage={15}
            rowsPerPageOptions={[15]}
            className={classes.paginationMobile}
          />
        )}
      </div>

      {/* Assign Plan Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{T("assignPlan")}</DialogTitle>
        <DialogContent>
          <TextField
            label={T("form.companyId")}
            type="number"
            fullWidth
            value={form.companyId}
            onChange={(e) => setForm({ ...form, companyId: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            style={{ marginTop: 8 }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={T("form.plan")}
            select
            fullWidth
            value={form.planId}
            onChange={(e) => setForm({ ...form, planId: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            inputProps={MOBILE_INPUT_PROPS}
          >
            {plans.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name} — {formatCurrency(p.value, p.currency || "ILS")}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={T("form.status")}
            select
            fullWidth
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            inputProps={MOBILE_INPUT_PROPS}
          >
            <MenuItem value="trial">Trial</MenuItem>
            <MenuItem value="active">Active</MenuItem>
          </TextField>
          <TextField
            label={T("form.periodMonths")}
            type="number"
            fullWidth
            value={form.periodMonths}
            onChange={(e) => setForm({ ...form, periodMonths: parseInt(e.target.value, 10) || 1 })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            inputProps={{ min: 1, max: 24, ...MOBILE_INPUT_PROPS }}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActionsMobile}>
          <Button onClick={() => setAssignOpen(false)}>{T("form.cancel")}</Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            color="primary"
            disabled={!form.companyId || !form.planId}
          >
            {T("form.assign")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{T("recordPayment")}</DialogTitle>
        <DialogContent>
          <TextField
            label={T("form.companyId")}
            type="number"
            fullWidth
            value={payForm.companyId}
            onChange={(e) => setPayForm({ ...payForm, companyId: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            style={{ marginTop: 8 }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={T("form.amount")}
            type="number"
            fullWidth
            value={payForm.amount}
            onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={T("form.currency")}
            select
            fullWidth
            value={payForm.currency}
            onChange={(e) => setPayForm({ ...payForm, currency: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            inputProps={MOBILE_INPUT_PROPS}
          >
            <MenuItem value="ILS">ILS (₪)</MenuItem>
            <MenuItem value="USD">USD ($)</MenuItem>
            <MenuItem value="EUR">EUR (€)</MenuItem>
          </TextField>
          <TextField
            label={T("form.description")}
            fullWidth
            value={payForm.description}
            onChange={(e) => setPayForm({ ...payForm, description: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={T("form.type")}
            select
            fullWidth
            value={payForm.type}
            onChange={(e) => setPayForm({ ...payForm, type: e.target.value })}
            className={`${classes.dialogField} ${classes.inputControl}`}
            variant="outlined"
            size="small"
            inputProps={MOBILE_INPUT_PROPS}
          >
            <MenuItem value="subscription">Subscription</MenuItem>
            <MenuItem value="one_time">One-time</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions className={classes.dialogActionsMobile}>
          <Button onClick={() => setPaymentOpen(false)}>{T("form.cancel")}</Button>
          <Button
            onClick={handleRecordPayment}
            variant="contained"
            color="primary"
            disabled={!payForm.companyId || !payForm.amount}
          >
            {T("form.record")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionsManager;
